import { useMutation } from '@apollo/client';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { useAppContext } from '../../../../data/context/app-context';
import {
  GetPatientItemsDocument,
  PatientItemDataFragment,
  ResendPatientItemWelcomeMailDocument,
} from '../../../../data/types/api/graphql';
import { AlertMessageType } from '../../../../elements/alert-message/alert-message';
import { CustomFormattedMessage } from '../../../../elements/intl/custom-formatted-message';
import { ModalPrompt } from '../../../../elements/modal/modal-prompt';

type PatientResendWelcomeMailPromptProps = {
  patientItem: PatientItemDataFragment;
  onSend?: (value: PatientItemDataFragment) => void;
  onClose?: () => void;
};

export function PatientResendWelcomeMailPrompt({
  patientItem,
  onSend,
  onClose,
}: PatientResendWelcomeMailPromptProps) {
  const intl = useIntl();

  const { showAlertMessage } = useAppContext();

  const [resendPatientItemWelcomeMail] = useMutation(
    ResendPatientItemWelcomeMailDocument,
    {
      refetchQueries: [GetPatientItemsDocument],
    }
  );

  const onHandleSend = useCallback(async () => {
    try {
      const result = await resendPatientItemWelcomeMail({
        variables: { ids: [patientItem.id] },
      });

      if (result.data?.patientResendWelcome) {
        showAlertMessage({
          type: AlertMessageType.SUCCESS,
          message: intl.formatMessage({
            id: 'patient.patient-resend-welcome-mail.success',
          }),
        });

        onSend?.(patientItem);

        onClose?.();
      } else {
        throw new Error('An unknown error has occurred in the server respond.');
      }
    } catch (error) {
      showAlertMessage({
        type: AlertMessageType.ERROR,
        message: (error as Error).message,
      });
    }
  }, [
    intl,
    patientItem,
    resendPatientItemWelcomeMail,
    showAlertMessage,
    onSend,
    onClose,
  ]);

  if (!patientItem) return null;

  return (
    <ModalPrompt
      testId="patient-delete"
      onConfirm={onHandleSend}
      onClose={onClose}
    >
      <p>
        <CustomFormattedMessage id="patient.patient-resend-welcome-mail.content" />
      </p>
    </ModalPrompt>
  );
}
